import React, {useRef, useState} from "react";
import {getGrowthDeliveries, retryGrowthDelivery} from "@/lib/apiClient";
import {useData} from "@/lib/useData";
import {userError} from "@/lib/userError";

const labels = {pending:"Odottaa lähetystä", sending:"Lähetetään — vastaanottoa ei vielä vahvistettu",
  retry_wait:"Lähetys jäi kesken — uusi yritys odottaa", failed:"Toimitusta ei vahvistettu",
  blocked:"Yhteys tai käyttöoikeus vaatii tarkistuksen", delivered:"Kasvujohtajan vastaanotto vahvistettu"};
const buttonClass = "min-h-11 rounded-full border border-border px-4 py-2 text-sm disabled:opacity-40";
const valid = item => /^growth-approved-[a-f0-9]{64}$/.test(item?.id) &&
  item.task_id === `growth-${item.id.slice("growth-approved-".length)}` &&
  typeof item.fingerprint === "string" && /^[a-f0-9]{64}$/.test(item.fingerprint) &&
  Object.hasOwn(labels,item.state) && Number.isInteger(item.attempts) && item.attempts >= 0 && item.attempts <= 2;

function Delivery({item, enabled, refetch}) {
  const lock = useRef(false);
  const [busy,setBusy] = useState(false), [error,setError] = useState(""), [notice,setNotice] = useState("");
  const retry = async () => {
    if (lock.current) return;
    lock.current = true; setBusy(true); setError(""); setNotice("");
    try {
      const result = await retryGrowthDelivery(item.id,item.fingerprint);
      if (result?.event_id !== item.id || !["queued","unchanged"].includes(result.status)) throw new Error("Kuittaus puuttuu.");
      setNotice(result.status === "queued" ? "Uusi yritys jonotettiin. Vastaanottoa ei ole vielä vahvistettu." : "Tilanne on muuttunut. Päivitetään toimitustila.");
      await refetch();
    } catch (failure) {setError(userError(failure,"Uutta yritystä ei voitu vahvistaa."));}
    finally {lock.current = false; setBusy(false);}
  };
  return <li className="space-y-2 rounded-xl border border-border p-3">
    <p className={`text-sm ${["failed","blocked"].includes(item.state)?"text-amber-400":"text-muted-foreground"}`}>{labels[item.state]}</p>
    <a href={`/tasks?task=${encodeURIComponent(item.task_id)}`} className="inline-flex min-h-11 items-center text-sm text-primary underline">Avaa päätöksen tehtävä</a>
    {["failed","blocked"].includes(item.state) && <button className={buttonClass} disabled={busy||!enabled} onClick={retry}>{busy?"Jonotetaan…":"Yritä tilapalautetta uudelleen"}</button>}
    {notice&&<p role="status" className="text-sm">{notice}</p>}
    {error&&<p role="alert" className="text-sm text-destructive">{error}</p>}
  </li>;
}

export default function GrowthDelivery() {
  const state = useData(getGrowthDeliveries,[],["growth"],{autoRefreshMs:60000});
  if (state.loading) return null;
  if (state.error) return <p role="alert" className="text-sm">Tilapalautteiden toimitusta ei voitu tarkistaa. <button className={buttonClass} onClick={state.refetch}>Päivitä toimitustila</button></p>;
  const data = state.data;
  if (!data || typeof data.delivery_enabled !== "boolean" || typeof data.has_more !== "boolean" || !Array.isArray(data.items) || !data.items.every(valid)) return <p role="alert">Tilapalautteiden tietoja ei voitu varmistaa.</p>;
  if (!data.items.length) return null;
  return <section className="space-y-3" aria-label="Päätösten toimitus Kasvujohtajalle">
    <h2 className="text-sm font-semibold">Päätösten toimitus Kasvujohtajalle</h2>
    {!data.delivery_enabled&&data.items.some(item=>item.state!=="delivered")&&<p role="status" className="text-sm text-amber-400">Tilapalautteiden lähetys on pois käytöstä. Jonossa olevia päätöksiä ei vielä toimiteta.</p>}
    <ul className="space-y-2">{data.items.map(item=><Delivery key={item.id} item={item} enabled={data.delivery_enabled} refetch={state.refetch}/>)}</ul>
    {data.has_more&&<p role="status" className="text-sm text-amber-400">Näytetään 20 vanhinta keskeneräistä palautetta. Muita palautteita voi vielä odottaa.</p>}
  </section>;
}
